import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@/components/ui";
import { generateSku } from "@/lib/utils";
import { notify } from "@/lib/notify";
import { useCreateProduct } from "../api";
import type { CreateVariantRequest } from "@/types";

type LocalVariant = {
  name: string;
  sku: string;
  price: number;
  costPrice: number;
  isDefault: boolean;
};

type FormState = {
  name: string;
  description: string;
  sku: string;
  imageUrl: string;
  categoryId: string;
  attributeIds: string[];
  variants: LocalVariant[];
};

const EMPTY_FORM: FormState = {
  name: "",
  description: "",
  sku: "",
  imageUrl: "",
  categoryId: "",
  attributeIds: [],
  variants: [
    { name: "Regular", sku: "", price: 0, costPrice: 0, isDefault: true },
  ],
};

interface ProductFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: { id: string; name: string }[];
  attributes: { id: string; name: string }[];
}

export function ProductFormDialog({
  open,
  onOpenChange,
  categories,
  attributes,
}: ProductFormDialogProps) {
  const createProduct = useCreateProduct();
  const [formData, setFormData] = useState<FormState>(EMPTY_FORM);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const variants: CreateVariantRequest[] = formData.variants.map(
        (v, i) => ({
          name: v.name,
          sku: v.sku || `${formData.sku}-${i + 1}`,
          price: Math.round(v.price * 100),
          costPrice: Math.round(v.costPrice * 100),
          isDefault: v.isDefault,
        }),
      );

      await createProduct.mutateAsync({
        name: formData.name,
        description: formData.description || undefined,
        sku: formData.sku,
        imageUrl: formData.imageUrl || undefined,
        categoryId: formData.categoryId || undefined,
        hasVariants: true,
        attributeIds:
          formData.attributeIds.length > 0 ? formData.attributeIds : undefined,
        variants,
      });
      notify.success("Product created");
      setFormData(EMPTY_FORM);
      onOpenChange(false);
    } catch (err) {
      notify.fromError(err, "Failed to create product");
    }
  };

  const addVariantLocal = () => {
    setFormData({
      ...formData,
      variants: [
        ...formData.variants,
        { name: "", sku: "", price: 0, costPrice: 0, isDefault: false },
      ],
    });
  };

  const removeVariantLocal = (index: number) => {
    if (formData.variants.length <= 1) {
      return;
    }

    const variants = formData.variants.filter((_, i) => i !== index);

    if (!variants.some((v) => v.isDefault) && variants[0]) {
      variants[0].isDefault = true;
    }

    setFormData({ ...formData, variants });
  };

  const updateVariantLocal = (
    index: number,
    field: keyof LocalVariant,
    value: string | number | boolean,
  ) => {
    const variants = [...formData.variants];
    const variant = variants[index];

    if (!variant) {
      return;
    }

    if (field === "isDefault" && value === true) {
      variants.forEach((v, i) => (v.isDefault = i === index));
    } else {
      (variant as Record<string, string | number | boolean>)[field] = value;
    }

    setFormData({ ...formData, variants });
  };

  const toggleAttribute = (attrId: string) => {
    const ids = formData.attributeIds.includes(attrId)
      ? formData.attributeIds.filter((id) => id !== attrId)
      : [...formData.attributeIds, attrId];
    setFormData({ ...formData, attributeIds: ids });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[700px]">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle>Create Product</DialogTitle>
        </DialogHeader>
        <ScrollArea className="min-h-0 flex-1">
          <form onSubmit={handleSubmit} className="space-y-4 p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      name,
                      sku:
                        prev.sku === "" || prev.sku === generateSku(prev.name)
                          ? generateSku(name)
                          : prev.sku,
                    }));
                  }}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sku">SKU *</Label>
                <Input
                  id="sku"
                  placeholder="Auto-generated from name"
                  value={formData.sku}
                  onChange={(e) =>
                    setFormData({ ...formData, sku: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={2}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.categoryId || "none"}
                  onValueChange={(v) =>
                    setFormData({
                      ...formData,
                      categoryId: v === "none" ? "" : v,
                    })
                  }
                >
                  <SelectTrigger id="category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Category</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="imageUrl">Image URL</Label>
                <Input
                  id="imageUrl"
                  value={formData.imageUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, imageUrl: e.target.value })
                  }
                  placeholder="https://..."
                />
              </div>
            </div>
            {attributes.length > 0 && (
              <div className="space-y-2">
                <Label>Attributes (Customizations)</Label>
                <div className="flex flex-wrap gap-2">
                  {attributes.map((attr) => (
                    <Badge
                      key={attr.id}
                      variant={
                        formData.attributeIds.includes(attr.id)
                          ? "default"
                          : "outline"
                      }
                      className="cursor-pointer"
                      onClick={() => toggleAttribute(attr.id)}
                    >
                      {attr.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Variants (Sizes/Options)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addVariantLocal}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add Variant
                </Button>
              </div>
              <div className="space-y-2">
                {formData.variants.map((variant, index) => (
                  <div key={index} className="rounded-md border p-3">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                      <div className="space-y-1.5">
                        <Label htmlFor={`create-variant-name-${index}`}>
                          Name
                        </Label>
                        <Input
                          id={`create-variant-name-${index}`}
                          placeholder="e.g., Small"
                          value={variant.name}
                          onChange={(e) =>
                            updateVariantLocal(index, "name", e.target.value)
                          }
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor={`create-variant-price-${index}`}>
                          Selling Price ($)
                        </Label>
                        <Input
                          id={`create-variant-price-${index}`}
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="e.g., 4.99"
                          value={variant.price || ""}
                          onChange={(e) =>
                            updateVariantLocal(
                              index,
                              "price",
                              parseFloat(e.target.value) || 0,
                            )
                          }
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor={`create-variant-cost-${index}`}>
                          Cost to Make ($)
                        </Label>
                        <Input
                          id={`create-variant-cost-${index}`}
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="e.g., 1.50"
                          value={variant.costPrice || ""}
                          onChange={(e) =>
                            updateVariantLocal(
                              index,
                              "costPrice",
                              parseFloat(e.target.value) || 0,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Checkbox
                          id={`create-variant-default-${index}`}
                          checked={variant.isDefault}
                          onCheckedChange={(checked) =>
                            updateVariantLocal(index, "isDefault", checked)
                          }
                        />
                        <Label
                          htmlFor={`create-variant-default-${index}`}
                          className="text-xs text-muted-foreground"
                        >
                          Default variant
                        </Label>
                      </div>
                      {formData.variants.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeVariantLocal(index)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Selling price is what the customer pays; cost to make is what
                it costs you, used for profit reporting.
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createProduct.isPending}>
                {createProduct.isPending ? "Saving..." : "Create Product"}
              </Button>
            </DialogFooter>
          </form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
